import { createFileRoute } from '@tanstack/react-router'

import { JellyIcon } from '@/components/icons'
import {
  BookingHelp,
  BookingPicker,
  PastShootRow,
  ShootPrep,
  ShootTicket,
} from '@/components/klient/foceni'
import { SectionLabel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import {
  BOOKED_SHOOT,
  BOOKING_DAYS,
  BOOKING_HELP,
  FIRST_SHOOT_INTRO,
  PAST_SHOOTS,
  PHOTOGRAPHER,
  PREP,
  SHOOT_QUOTA,
} from '@/mocks/klient/foceni'

/**
 * Three states, all linkable:
 * - default — a shoot is booked, so the ticket is the screen,
 * - `novy` — the routine booking a client does roughly once a month,
 * - `prvni` — their very first shoot, which is a different conversation.
 */
type FoceniSearch = { stav?: 'novy' | 'prvni' }

export const Route = createFileRoute('/klient/foceni/')({
  validateSearch: (search: Record<string, unknown>): FoceniSearch =>
    search.stav === 'novy' || search.stav === 'prvni' ? { stav: search.stav } : {},
  head: () => ({ meta: [{ title: 'Focení · Aplikace Bitez' }] }),
  component: KlientFoceni,
})

/**
 * The Focení tab, for the whole life of the account: the top half is either the
 * booked shoot or the picker for the next one (never both — owner's rule, no
 * planning ahead), and the history hangs underneath.
 *
 * The first booking and the twentieth are deliberately not the same screen. A
 * first-timer has never had a photographer in their kitchen and needs to know
 * what comes out of it, how long it takes and whether they have to cook; a
 * returning client wants a date and would read the same three paragraphs as
 * being talked down to. So the explanations are **expanded the first time and
 * folded away afterwards** — never deleted, because people forget.
 */
function KlientFoceni() {
  const { stav } = Route.useSearch()
  const first = stav === 'prvni'
  const picking = first || stav === 'novy'

  return (
    <div className="px-4 pt-6 pb-6">
      {picking ? (
        <>
          {/* A question is not a title: it tells you what to do next. The booked
              state gets no heading at all — the ticket already says the date. */}
          <h1 className="font-display text-3xl leading-[0.9] uppercase">
            {first ? 'Kdy k vám Tomáš vyrazí poprvé?' : 'Kdy má Tomáš přijet?'}
          </h1>

          {/* What you get and where you stand — as two chips. Under a question,
              two sentences of the same thing read as terms and conditions, and
              the explaining belongs further down, where it is asked for. */}
          <div className="mt-3.5 flex flex-wrap items-center gap-2">
            <span className="-rotate-1 rounded-full bg-sun px-3 py-1 text-[11px] font-bold">
              {SHOOT_QUOTA.included}
            </span>
            <span className="rounded-full bg-surface px-3 py-1 text-[11px] font-semibold text-ink/60">
              {SHOOT_QUOTA.state}
            </span>
          </div>

          <section className="animate-rise-up mt-6">
            <BookingPicker
              photographer={PHOTOGRAPHER}
              days={BOOKING_DAYS}
              selectedDay="17. 4."
              selectedSlot="10:00"
              first={first}
            />
          </section>

          {first && (
            <div className="animate-rise-up mt-8" style={{ animationDelay: '90ms' }}>
              <ShootPrep title="Jak focení probíhá" intro={FIRST_SHOOT_INTRO} steps={PREP} />
            </div>
          )}

          {/* Not finding a suitable time is not a beginner's problem, so the way
              out is on every booking — the explanations above are not. */}
          <div className="animate-rise-up mt-7" style={{ animationDelay: '120ms' }}>
            <BookingHelp help={BOOKING_HELP} />

            {/* Spec §6.3, in the only moment it makes sense: you are arranging
                the next shoot with someone you have already met. Not on the
                first booking (nobody has been here yet) and not on the booked
                ticket, where it would read as "replace this man" next to his
                face. It starts as a conversation, because the swap is confirmed
                by the správce, not by a button. */}
            {!first && (
              <p className="mt-4 text-center">
                <Link
                  href="/klient/chat"
                  className="text-[11px] font-semibold text-ink/45 underline-offset-4 hover:text-brand hover:underline"
                >
                  Chci jiného fotografa
                </Link>
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="animate-rise-up">
          <ShootTicket shoot={BOOKED_SHOOT} photographer={PHOTOGRAPHER} />
        </div>
      )}

      {/* History lives under every state that has one — this is the client's
          shoot archive as much as it is the booking screen. Before the first
          shoot there is nothing to archive, so the section is gone rather than
          empty. */}
      {!first && (
        <section className="animate-rise-up mt-8" style={{ animationDelay: '150ms' }}>
          <SectionLabel>Proběhlá focení</SectionLabel>
          <div className="mt-4 space-y-4">
            {PAST_SHOOTS.map((shoot) => (
              <PastShootRow key={shoot.month} shoot={shoot} />
            ))}
          </div>
        </section>
      )}

      {/* Extra focení is an upsell on top of what you already have — it only
          makes sense once this month's shoot is actually booked, so the picker
          never shows it. */}
      {!picking && (
        <footer className="mt-8 border-t border-ink/10 pt-4">
          <p className="text-xs leading-relaxed text-ink/50">
            Potřebujete fotit častěji, než máte v balíčku?{' '}
            <button
              type="button"
              className="cursor-pointer font-semibold text-brand underline-offset-4 hover:underline"
            >
              Domluvte si extra focení
            </button>
          </p>
        </footer>
      )}
    </div>
  )
}
